import { useState } from "react";
import { ChevronDown, ChevronRight, Calculator, BarChart, TrendingDown, Calendar, Fuel, Zap, Thermometer, Filter, Target, Activity } from "lucide-react";

const AdditiveCalculations = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const dataSources = [
    {
      collection: "VesselPerformance",
      fields: [
        "recordedAt", "heavyFuelOil", "lightFuelOil", "marineGasOil", "others",
        "additivesName", "additivesConsumption", "createdAt", "vessel"
      ],
      purpose: "Fuel consumption and additive usage records"
    },
    {
      collection: "Vessel",
      fields: ["_id", "name", "imoNumber"],
      purpose: "Vessel identification and metadata"
    },
    {
      collection: "FuelTable",
      fields: ["Fuel_Type", "CF_C02", "CF_CH4", "CF_N20"],
      purpose: "Emission factors for different fuel types"
    },
    {
      collection: "VesselStatus",
      fields: ["statusTime", "additive_used", "additive_consumption", "vessel"],
      purpose: "Real-time additive usage tracking"
    }
  ];

  const coreCalculations = [
    {
      id: "date-range-calculation",
      title: "Date Range Calculation",
      description: "Calculates symmetric date ranges before/after additive start date",
      formula: "Before Period = Symmetric period before query range",
      subFormulas: [
        "Total Days = (End Date - Start Date) + 1",
        "Before End Date = Start Date - 1 day",
        "Before Start Date = Before End Date - Total Days + 1",
        "After Period = User query range (unchanged)"
      ],
      icon: <Calendar className="w-5 h-5" />,
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: "first-additive-detection",
      title: "First Additive Usage Detection",
      description: "Identifies the first time additives were used on a vessel",
      formula: "First Additive Date = min(recordedAt) where additivesName ≠ ''",
      subFormulas: [
        "Query: additivesName ≠ '' AND additivesConsumption ≠ ''",
        "Sort by: recordedAt ascending",
        "Take first record",
        "Use this as baseline for all comparisons"
      ],
      icon: <Target className="w-5 h-5" />,
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "fuel-consumption-aggregation",
      title: "Fuel Consumption Aggregation",
      description: "Aggregates fuel consumption by period and fuel type",
      formula: "Total Fuel = Σ(HFO + LFO + MGO + Others)",
      subFormulas: [
        "HFO = heavyFuelOil (MT)",
        "LFO = lightFuelOil (MT)", 
        "MGO = marineGasOil (MT)",
        "Others = others (MT)",
        "Sum across all records in period",
        "Group by: before/after periods"
      ],
      icon: <Fuel className="w-5 h-5" />,
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
    {
      id: "percentage-change-calculation",
      title: "Percentage Change Calculation",
      description: "Calculates fuel consumption change between periods",
      formula: "% Change = ((After - Before) ÷ Before) × 100",
      subFormulas: [
        "Positive value = Increased consumption",
        "Negative value = Decreased consumption",
        "Zero = No change",
        "NaN = No baseline data available"
      ],
      icon: <TrendingDown className="w-5 h-5" />,
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    }
  ];

  const emissionCalculations = [
    {
      id: "co2-emissions",
      title: "CO₂ Emissions Calculation",
      description: "Calculates CO₂ emissions from fuel consumption",
      formula: "CO₂ = Fuel Quantity × CO₂ Emission Factor",
      subFormulas: [
        "HFO CO₂ = heavyFuelOil × CF_C02 (from FuelTable)",
        "LFO CO₂ = lightFuelOil × CF_C02 (from FuelTable)",
        "MGO CO₂ = marineGasOil × CF_C02 (from FuelTable)",
        "Total CO₂ = Sum across all fuel types"
      ],
      icon: <Activity className="w-5 h-5" />,
      borderColor: "border-red-200",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
    },
    {
      id: "ch4-emissions",
      title: "CH₄ Emissions Calculation",
      description: "Calculates methane emissions from fuel consumption",
      formula: "CH₄ = Fuel Quantity × CH₄ Emission Factor",
      subFormulas: [
        "HFO CH₄ = heavyFuelOil × CF_CH4 (from FuelTable)",
        "LFO CH₄ = lightFuelOil × CF_CH4 (from FuelTable)", 
        "MGO CH₄ = marineGasOil × CF_CH4 (from FuelTable)",
        "Total CH₄ = Sum across all fuel types"
      ],
      icon: <Zap className="w-5 h-5" />,
      borderColor: "border-teal-200",
      bgColor: "bg-teal-50",
      textColor: "text-teal-700",
    },
    {
      id: "n2o-emissions",
      title: "N₂O Emissions Calculation",
      description: "Calculates nitrous oxide emissions from fuel consumption",
      formula: "N₂O = Fuel Quantity × N₂O Emission Factor",
      subFormulas: [
        "HFO N₂O = heavyFuelOil × CF_N20 (from FuelTable)",
        "LFO N₂O = lightFuelOil × CF_N20 (from FuelTable)",
        "MGO N₂O = marineGasOil × CF_N20 (from FuelTable)",
        "Total N₂O = Sum across all fuel types"
      ],
      icon: <Thermometer className="w-5 h-5" />,
      borderColor: "border-indigo-200",
      bgColor: "bg-indigo-50",
      textColor: "text-indigo-700",
    },
    {
      id: "total-emissions",
      title: "Total Emissions Calculation",
      description: "Calculates total greenhouse gas emissions",
      formula: "Total Emissions = CO₂ + CH₄ + N₂O",
      subFormulas: [
        "CO₂e (CO₂ equivalent) calculation available",
        "Global Warming Potential (GWP) factors:",
        "CH₄ GWP = 28 × CH₄ emissions",
        "N₂O GWP = 265 × N₂O emissions"
      ],
      icon: <BarChart className="w-5 h-5" />,
      borderColor: "border-violet-200",
      bgColor: "bg-violet-50",
      textColor: "text-violet-700",
    }
  ];

  const additiveUsageCalculations = [
    {
      id: "additive-tracking",
      title: "Additive Usage Tracking",
      description: "Tracks additive consumption over time",
      formula: "Additive Consumption = Σ(additive_consumption)",
      subFormulas: [
        "From VesselStatus collection",
        "Multiple additives per record possible",
        "Sum across all status records in period",
        "Group by additive name"
      ],
      icon: <Filter className="w-5 h-5" />,
      borderColor: "border-pink-200",
      bgColor: "bg-pink-50",
      textColor: "text-pink-700",
    },
    {
      id: "additive-effectiveness",
      title: "Additive Effectiveness Analysis",
      description: "Analyzes fuel consumption changes with additives",
      formula: "Effectiveness = Fuel reduction ÷ Additive consumption",
      subFormulas: [
        "Compare before/after fuel consumption",
        "Account for additive usage period",
        "Calculate cost-benefit ratio",
        "Track trends over time"
      ],
      icon: <Target className="w-5 h-5" />,
      borderColor: "border-orange-200",
      bgColor: "bg-orange-50",
      textColor: "text-orange-700",
    }
  ];

  const emissionFactors = [
    { fuel: "HFO", co2: 3.114, ch4: 0.00005, n2o: 0.00018, source: "FuelTable" },
    { fuel: "LFO", co2: 3.151, ch4: 0.00005, n2o: 0.00018, source: "FuelTable" },
    { fuel: "MGO", co2: 3.206, ch4: 0.00005, n2o: 0.00018, source: "FuelTable" },
  ];

  const calculationProcess = [
    { step: 1, title: "Find First Additive Date", description: "Query VesselPerformance for first additive usage record" },
    { step: 2, title: "Calculate Date Ranges", description: "Determine symmetric before/after periods for comparison" },
    { step: 3, title: "Query Fuel Consumption", description: "Aggregate fuel consumption for both periods" },
    { step: 4, title: "Calculate Emissions", description: "Apply emission factors to fuel consumption data" },
    { step: 5, title: "Calculate Percentage Change", description: "Compare before/after fuel and emissions" },
    { step: 6, title: "Track Additive Usage", description: "Aggregate additive consumption from VesselStatus" },
    { step: 7, title: "Compile Results", description: "Generate comprehensive comparison report" },
    { step: 8, title: "Data Quality Check", description: "Validate data completeness and accuracy" },
  ];

  const queryExamples = [
    {
      endpoint: "/api/additives/first-date/:vesselId",
      method: "GET",
      description: "Get first additive usage date",
      query: `VesselPerformance.findOne({
  vessel: vesselId,
  additivesName: { $ne: "" },
  additivesConsumption: { $ne: "" }
}).sort({ recordedAt: 1 })`
    },
    {
      endpoint: "/api/additives/metadata/:vesselId",
      method: "GET",
      description: "Get comprehensive additive metadata",
      query: `VesselPerformance.aggregate([
  { $match: { vessel: vesselId, recordedAt: { $gte: beforeStart, $lte: beforeEnd } } },
  { $group: { _id: null, totalHeavyFuelOil: { $sum: "$heavyFuelOil" } } }
])`
    },
    {
      endpoint: "/api/additives/emissions/:vesselId",
      method: "GET",
      description: "Get emission comparisons",
      query: `FuelTable.find({ Fuel_Type: { $in: ["HFO", "LFO", "MGO"] } })`
    }
  ];

  const dataQualityChecks = [
    { check: "Has Baseline Data", condition: "beforeAdditive.length > 0", impact: "Comparison validity" },
    { check: "Has After Data", condition: "afterAdditive.length > 0", impact: "Effectiveness analysis" },
    { check: "Additive Records Exist", condition: "firstAdditiveRecord !== null", impact: "Period determination" },
    { check: "Date Ranges Symmetric", condition: "beforePeriod.days = afterPeriod.days", impact: "Fair comparison" },
    { check: "Emission Factors Available", condition: "fuelData.length > 0", impact: "Emissions calculation" },
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#d5e7f7] rounded-lg">
              <Calculator className="w-6 h-6 text-[#566573]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Additive Performance Calculations
              </h2>
              <p className="text-gray-600 text-sm">
                Fuel additive impact analysis, emissions tracking, and performance metrics
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200 space-y-8">
            {/* Data Sources Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Data Sources & Collections</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {dataSources.map((source, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-gray-800">{source.collection}</h4>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">{source.purpose}</p>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div className="font-medium mb-1">Key Fields:</div>
                      {source.fields.map((field, idx) => (
                        <div key={idx} className="font-mono truncate">• {field}</div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Core Calculations */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Core Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {coreCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Calculation:</p>
                        <div className="text-sm bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </div>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Details:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100 font-mono">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Emission Calculations */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Emission Calculations</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {emissionCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Formula:</p>
                        <div className="text-sm bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </div>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Implementation:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Additive Usage Calculations */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Additive Usage & Effectiveness</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {additiveUsageCalculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex items-start gap-3 mb-4">
                      <div className={`p-2 rounded-lg ${calc.textColor} bg-white`}>
                        {calc.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-bold text-gray-800 mb-1 ${calc.textColor}`}>
                          {calc.title}
                        </h4>
                        <p className="text-sm text-gray-600">{calc.description}</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">Method:</p>
                        <div className="text-sm bg-white p-3 rounded border border-gray-200 font-mono">
                          {calc.formula}
                        </div>
                      </div>
                      
                      {calc.subFormulas && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Details:</p>
                          <div className="space-y-2">
                            {calc.subFormulas.map((subFormula, idx) => (
                              <div key={idx} className="text-sm bg-white p-2 rounded border border-gray-100">
                                {subFormula}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Calculation Process */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Calculation Process Flow</h3>
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {calculationProcess.map((step) => (
                    <div key={step.step} className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                          {step.step}
                        </div>
                        <div className="font-semibold text-gray-800">{step.title}</div>
                      </div>
                      <p className="text-sm text-gray-600">{step.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Complete Example */}
            <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-5 rounded-lg border border-emerald-200">
              <h4 className="font-semibold text-emerald-800 mb-3">Complete Calculation Example</h4>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium text-emerald-700 mb-1">Input Data:</div>
                    <div className="text-xs space-y-1">
                      <div>• First Additive Date: 2024-01-15</div>
                      <div>• Query Range: 2024-02-01 to 2024-02-28</div>
                      <div>• Before HFO: 500 MT</div>
                      <div>• After HFO: 450 MT</div>
                      <div>• Additive Used: EcoBoost-500</div>
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium text-emerald-700 mb-1">Intermediate Calculations:</div>
                    <div className="text-xs space-y-1">
                      <div>• Before Period: 2024-01-01 to 2024-01-14</div>
                      <div>• Total Fuel Before: 750 MT</div>
                      <div>• Total Fuel After: 675 MT</div>
                      <div>• CO₂ Before: 750 × 3.114 = 2,336 t</div>
                      <div>• CO₂ After: 675 × 3.114 = 2,102 t</div>
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium text-emerald-700 mb-1">Final Results:</div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-green-600">Fuel Reduction: -10%</div>
                      <div className="text-sm text-green-500">CO₂ Reduction: -234 t</div>
                      <div className="text-xs text-gray-600 mt-2">Additive Effectiveness: Positive</div>
                    </div>
                  </div>
                </div>
                <div className="text-sm text-emerald-700 bg-white p-3 rounded border">
                  <span className="font-medium">Interpretation:</span> The additive resulted in a 10% reduction in fuel consumption 
                  and 234 tonnes of CO₂ savings during the comparison period, indicating positive additive effectiveness.
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdditiveCalculations;